import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import type {
  HoverIdleSnapshot,
  HoverIdleTargetCellBaseSnapshot,
} from "../types";
import { HoverIntentController } from "@/features/hoverIdle/core/hoverIntentController";
import type { HoverPointerModel } from "../input/hoverPointerModel";
import {
  computeMpmAvailableMpFromOccupancy,
  isMpmHovTargetsActive,
} from "@/features/hoverIdle/mpm/mpmCapacity";
import { resolveDirectMpTargetViewState } from "@/features/hoverIdle/mpm/directMpTargetViewState";
import { syncDirectMpSlot } from "@/features/hoverIdle/mpm/directMpSlotView";
import { MpSlotChargeController } from "@/features/hoverIdle/mpm/mpSlotChargeController";
import { applyHovThemeHue } from "../nHov/nHovThemeCss";
import { MOUSE_POINTER_ICON_SVG_SOURCE } from "@/shared/ui/mousePointerIcon";
import banIconSource from "bootstrap-icons/icons/ban.svg?raw";
import pauseIconSource from "bootstrap-icons/icons/pause-fill.svg?raw";
import {
  resolveTimConsumptionDashCycleDurationMs,
  TIM_CONSUMPTION_DASH_SPEED_POLICY,
} from "./timConsumptionDashAnimation";

const TOTAL_CELLS = 9;
const COLOR_PROGRESS_MAX_LEVEL = 10;
const HOV_CELL_COLOR_STEPS = 10;
const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

function buildHoverAnchorTargetId(
  variant: HoverTargetGridVariant,
  cellIndex: number,
): string {
  return `${variant}-cell-${cellIndex}`;
}

function createTimConsumptionDashTrack(): SVGSVGElement {
  const dashTrack = document.createElementNS(SVG_NAMESPACE, "svg");
  dashTrack.setAttribute(
    "class",
    "hover-idle-target-cell-tim-consumption-track",
  );
  dashTrack.setAttribute("aria-hidden", "true");
  dashTrack.setAttribute("focusable", "false");
  dashTrack.style.setProperty(
    "--tim-consumption-dash-length",
    `${TIM_CONSUMPTION_DASH_SPEED_POLICY.dashLengthPx}px`,
  );
  dashTrack.style.setProperty(
    "--tim-consumption-dash-gap",
    `${TIM_CONSUMPTION_DASH_SPEED_POLICY.dashGapPx}px`,
  );
  dashTrack.style.setProperty(
    "--tim-consumption-dash-phase-offset",
    `-${TIM_CONSUMPTION_DASH_SPEED_POLICY.dashLengthPx + TIM_CONSUMPTION_DASH_SPEED_POLICY.dashGapPx}px`,
  );

  const dashRect = document.createElementNS(SVG_NAMESPACE, "rect");
  dashRect.setAttribute("class", "hover-idle-target-cell-tim-consumption-dash");
  dashRect.setAttribute("x", "1");
  dashRect.setAttribute("y", "1");
  dashRect.setAttribute("width", "calc(100% - 2px)");
  dashRect.setAttribute("height", "calc(100% - 2px)");
  dashRect.setAttribute("rx", "5");
  dashRect.setAttribute("ry", "5");
  dashTrack.append(dashRect);

  return dashTrack;
}

export type HoverTargetGridVariant = "hov" | "tim";

export function orderHoverTargetRefreshIndices(
  cellCount: number,
  previousHoveringCellIndex: number | null,
): readonly number[] {
  const indices = Array.from({ length: cellCount }, (_, index) => index);
  if (
    previousHoveringCellIndex === null ||
    previousHoveringCellIndex < 0 ||
    previousHoveringCellIndex >= cellCount
  ) {
    return indices;
  }
  return [
    previousHoveringCellIndex,
    ...indices.filter((index) => index !== previousHoveringCellIndex),
  ];
}

interface HoverTargetGridViewOptions {
  readonly variant?: HoverTargetGridVariant;
  readonly pointerModel: HoverPointerModel;
  readonly getGridTier: (snapshot: HoverIdleSnapshot) => number;
  readonly getHoveringCellIndex: (snapshot: HoverIdleSnapshot) => number | null;
  readonly getThemeHue?: (snapshot: HoverIdleSnapshot) => number | null;
  readonly getTargetCells: (
    snapshot: HoverIdleSnapshot,
  ) => readonly HoverIdleTargetCellBaseSnapshot[];
  readonly getTimHovConsumptionRatePerSecond?: (
    snapshot: HoverIdleSnapshot,
    cellIndex: number,
  ) => number;
  readonly onCellHoverChange: (
    cellIndex: number,
    isHovering: boolean,
  ) => boolean | void;
  readonly getMpCapacity?: (snapshot: HoverIdleSnapshot) => number;
  readonly onMpPlace?: (cellIndex: number) => void;
  readonly onMpRetrieve?: (cellIndex: number) => void;
  readonly onMpWallAssign?: (cellIndex: number) => void;
  readonly onMpWallRemove?: (cellIndex: number) => void;
  readonly onCellClick?: (cellIndex: number) => void;
  readonly getHoverToggleDurationMs?: () => number;
}

interface CellNodes {
  readonly index: number;
  readonly element: HTMLDivElement;
  readonly lockBadge: HTMLSpanElement;
  readonly indexLabel: HTMLSpanElement;
  readonly intent: HoverIntentController;
  readonly mpSlot: MpSlotNodes | null;
  readonly wallMpSlot: MpSlotNodes | null;
  readonly cleanup: () => void;
}

interface MpSlotNodes {
  readonly element: HTMLDivElement;
  readonly controller: MpSlotChargeController;
}

export class HoverTargetGridView implements Disposable {
  readonly root: HTMLDivElement;
  private readonly cells: readonly CellNodes[];
  private readonly variant: HoverTargetGridVariant;
  private readonly pointerModel: HoverPointerModel;
  private readonly getGridTier: (snapshot: HoverIdleSnapshot) => number;
  private readonly getHoveringCellIndex: (
    snapshot: HoverIdleSnapshot,
  ) => number | null;
  private readonly getThemeHue:
    | ((snapshot: HoverIdleSnapshot) => number | null)
    | null;
  private readonly getTargetCells: (
    snapshot: HoverIdleSnapshot,
  ) => readonly HoverIdleTargetCellBaseSnapshot[];
  private readonly getTimHovConsumptionRatePerSecond:
    | ((snapshot: HoverIdleSnapshot, cellIndex: number) => number)
    | null;
  private readonly getMpCapacity:
    | ((snapshot: HoverIdleSnapshot) => number)
    | null;
  private readonly onMpPlace: ((cellIndex: number) => void) | null;
  private readonly onMpRetrieve: ((cellIndex: number) => void) | null;
  private readonly onMpWallAssign: ((cellIndex: number) => void) | null;
  private readonly onMpWallRemove: ((cellIndex: number) => void) | null;
  private readonly getHoverToggleDurationMs: (() => number) | null;
  private lastGridTier: number = -1;
  private isInputLocked = false;
  private readonly cellInteractivity = Array.from(
    { length: TOTAL_CELLS },
    (_, index) => index === 0,
  );

  constructor(options: HoverTargetGridViewOptions) {
    const messages = getMessages();
    this.variant = options.variant ?? "hov";
    this.pointerModel = options.pointerModel;
    this.getGridTier = options.getGridTier;
    this.getHoveringCellIndex = options.getHoveringCellIndex;
    this.getThemeHue = options.getThemeHue ?? null;
    this.getTargetCells = options.getTargetCells;
    this.getTimHovConsumptionRatePerSecond =
      options.getTimHovConsumptionRatePerSecond ?? null;
    this.getMpCapacity = options.getMpCapacity ?? null;
    this.onMpPlace = options.onMpPlace ?? null;
    this.onMpRetrieve = options.onMpRetrieve ?? null;
    this.onMpWallAssign = options.onMpWallAssign ?? null;
    this.onMpWallRemove = options.onMpWallRemove ?? null;
    this.getHoverToggleDurationMs = options.getHoverToggleDurationMs ?? null;
    const mpSupported =
      this.variant === "hov" &&
      this.getMpCapacity !== null &&
      this.onMpPlace !== null &&
      this.onMpRetrieve !== null;

    this.root = document.createElement("div");
    const variantClass =
      this.variant === "tim" ? "hover-idle-target-tim-target" : "";
    this.root.className =
      `hover-idle-target hover-idle-target-grid ${variantClass}`.trim();
    this.root.dataset.gridTier = "1";
    this.root.dataset.gridVariant = this.variant;

    const cells: CellNodes[] = [];
    for (let i = 0; i < TOTAL_CELLS; i += 1) {
      const cellIndex = i;
      const element = document.createElement("div");
      element.className = "hover-idle-target-cell";
      element.dataset.cellIndex = String(cellIndex);
      element.dataset.hoverAnchorTargetId = buildHoverAnchorTargetId(
        this.variant,
        cellIndex,
      );
      element.dataset.hovering = "false";
      element.dataset.unlocked = cellIndex === 0 ? "true" : "false";
      element.dataset.mpAssigned = "false";
      element.dataset.mpOperative = "false";
      element.dataset.mpBlocked = "false";
      element.dataset.blocked = "false";
      element.dataset.timConsuming = "false";
      element.tabIndex = 0;
      element.setAttribute("role", "button");
      element.setAttribute(
        "aria-label",
        this.variant === "tim"
          ? messages.hoverTarget.timCellAria(cellIndex + 1)
          : messages.hoverTarget.hovCellAria(cellIndex + 1),
      );

      const visual = document.createElement("div");
      visual.className = "hover-idle-target-cell-visual";
      visual.setAttribute("aria-hidden", "true");

      const indexLabel = document.createElement("span");
      indexLabel.className = "hover-idle-target-cell-index";

      const lockBadge = document.createElement("span");
      lockBadge.className = "hover-idle-target-cell-lock";
      lockBadge.textContent = messages.hoverTarget.lockBadge;
      lockBadge.setAttribute("aria-hidden", "true");

      if (this.variant === "hov") {
        visual.append(createTimConsumptionDashTrack());
      }

      visual.append(indexLabel, lockBadge);
      element.append(visual);

      const intent = new HoverIntentController({
        target: element,
        pointerModel: this.pointerModel,
        initiallyEnabled: this.cellInteractivity[cellIndex],
        onHoverChange: (isHovering) => {
          if (isHovering) {
            if (this.isInputLocked || !this.cellInteractivity[cellIndex]) {
              return false;
            }
            return options.onCellHoverChange(cellIndex, true);
          }
          // Global input locking owns its model clear at the HovView boundary.
          // Locally accept the controller deactivation even when no callback is
          // forwarded, so the controller never retains active hover while locked.
          if (this.isInputLocked) {
            return true;
          }
          return options.onCellHoverChange(cellIndex, false);
        },
      });

      const onCellClick = options.onCellClick;
      let cleanup = (): void => undefined;
      if (onCellClick) {
        const handleClick = (): void => {
          if (this.isInputLocked) return;
          onCellClick(cellIndex);
        };
        const handleKeyActivate = (event: KeyboardEvent): void => {
          if (event.key !== " " && event.key !== "Enter") return;
          event.preventDefault();
          if (this.isInputLocked) return;
          onCellClick(cellIndex);
        };
        element.addEventListener("click", handleClick);
        element.addEventListener("keydown", handleKeyActivate);
        cleanup = (): void => {
          element.removeEventListener("click", handleClick);
          element.removeEventListener("keydown", handleKeyActivate);
        };
      }

      const mpSlot: MpSlotNodes | null = mpSupported
        ? this.createMpSlot(cellIndex)
        : null;
      if (mpSlot !== null) {
        element.append(mpSlot.element);
      }

      const wallMpSlot =
        this.variant === "hov" &&
        this.onMpWallAssign !== null &&
        this.onMpWallRemove !== null
          ? this.createMpSlot(cellIndex, "wall")
          : null;
      if (wallMpSlot !== null) element.append(wallMpSlot.element);

      cells.push({
        index: cellIndex,
        element,
        lockBadge,
        indexLabel,
        intent,
        mpSlot,
        wallMpSlot,
        cleanup,
      });
      this.root.append(element);
    }
    this.cells = cells;
  }

  private createMpSlot(
    cellIndex: number,
    scope: "mpm" | "wall" = "mpm",
  ): MpSlotNodes {
    const messages = getMessages();
    const element = document.createElement("div");
    element.className = `hover-idle-mp-slot ${scope === "wall" ? "hover-idle-mp-slot--wall-corner" : "hover-idle-mp-slot--target-corner"}`;
    element.dataset.mpScope = scope;
    if (scope === "wall") {
      element.tabIndex = 0;
      element.setAttribute("role", "button");
      element.dataset.virtualPointer = "allow";
    }
    element.dataset.mpAssigned = "false";
    element.dataset.mpOperative = "false";
    element.dataset.mpBlocked = "false";
    element.dataset.paused = "false";
    element.dataset.blocked = "false";
    element.hidden = true;
    element.setAttribute(
      "aria-label",
      scope === "wall"
        ? messages.hoverTarget.wallMpSlotAria(`H${cellIndex + 1}`)
        : messages.hoverTarget.mpSlotAria(cellIndex + 1),
    );

    const icon = document.createElement("span");
    icon.className = "hover-idle-mp-slot-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = MOUSE_POINTER_ICON_SVG_SOURCE;

    const fill = document.createElement("span");
    fill.className = "hover-idle-mp-slot-fill";
    fill.setAttribute("aria-hidden", "true");

    const pauseOverlay = document.createElement("span");
    pauseOverlay.className = "hover-idle-mp-slot-pause-overlay";
    pauseOverlay.setAttribute("aria-hidden", "true");
    pauseOverlay.innerHTML = pauseIconSource;

    const blockOverlay = document.createElement("span");
    blockOverlay.className = "hover-idle-mp-slot-block-overlay";
    blockOverlay.setAttribute("aria-hidden", "true");
    blockOverlay.innerHTML = banIconSource;

    element.append(fill, icon, pauseOverlay, blockOverlay);

    const controller = new MpSlotChargeController({
      element,
      pointerModel: this.pointerModel,
      getChargeMs: this.getHoverToggleDurationMs
        ? () => this.getHoverToggleDurationMs!()
        : undefined,
      onAssign: () =>
        scope === "wall"
          ? this.onMpWallAssign?.(cellIndex)
          : this.onMpPlace?.(cellIndex),
      onRemove: () =>
        scope === "wall"
          ? this.onMpWallRemove?.(cellIndex)
          : this.onMpRetrieve?.(cellIndex),
    });
    return { element, controller };
  }

  update(
    snapshot: HoverIdleSnapshot,
    options: {
      readonly refreshHoverIntentsAfterStructureChange?: boolean;
    } = {},
  ): void {
    const tier = this.getGridTier(snapshot);
    const targetCells = this.getTargetCells(snapshot);
    const hoveringIndex = this.getHoveringCellIndex(snapshot);
    const themeHue = this.getThemeHue?.(snapshot) ?? null;
    const mpCapacity = this.getMpCapacity?.(snapshot) ?? 0;
    const availableMpSlots = computeMpmAvailableMpFromOccupancy({
      mpCapacity,
      state: snapshot.mpm,
    });
    const canAssignMpToHovTargets = isMpmHovTargetsActive(snapshot.mpm);
    const gridTierChanged = tier !== this.lastGridTier;
    let interactivityChanged = false;
    if (gridTierChanged) {
      this.root.dataset.gridTier = String(tier);
      this.lastGridTier = tier;
    }
    this.updateThemeHue(themeHue);
    for (let i = 0; i < TOTAL_CELLS; i += 1) {
      const nodes = this.cells[i];
      const visible = i < tier;
      nodes.element.hidden = !visible;
      if (!visible) {
        if (this.cellInteractivity[i]) {
          this.cellInteractivity[i] = false;
          interactivityChanged = true;
        }
        nodes.element.dataset.hovering = "false";
        nodes.element.dataset.unlocked = "false";
        nodes.element.dataset.mpAssigned = "false";
        nodes.element.dataset.mpOperative = "false";
        nodes.element.dataset.mpBlocked = "false";
        delete nodes.element.dataset.mpBlockedReason;
        nodes.element.dataset.blocked = "false";
        nodes.element.dataset.timConsuming = "false";
        nodes.element.style.removeProperty(
          "--tim-consumption-dash-cycle-duration",
        );
        if (nodes.mpSlot !== null) syncDirectMpSlot(nodes.mpSlot, null);
        if (nodes.wallMpSlot !== null) syncDirectMpSlot(nodes.wallMpSlot, null);
        continue;
      }
      const cellSnap = targetCells[i];
      const unlocked = cellSnap?.unlocked ?? i === 0;
      if (this.cellInteractivity[i] !== unlocked) {
        this.cellInteractivity[i] = unlocked;
        interactivityChanged = true;
      }
      const progress = this.resolveCellProgress(
        i,
        cellSnap?.purchasedUpgrades ?? 0,
      );
      const isFilled = cellSnap?.mpPlaced ?? false;
      const directMpState = resolveDirectMpTargetViewState({
        assigned: isFilled,
        targetActive: unlocked,
        sectionEnabled: canAssignMpToHovTargets,
        canAssign: availableMpSlots > 0,
        directMpBlockedByActiveWall:
          snapshot.hovTargetDirectMpBlockedByActiveWall,
        directMpBlockedReason: snapshot.hovTargetDirectMpBlockedReason,
        visualWhenInactive: "locked",
      });

      const wallAssignments = snapshot.wall.mpWallAssignments;
      const wallMpState =
        wallAssignments == null
          ? null
          : resolveDirectMpTargetViewState({
              assigned:
                wallAssignments.kind === "hov-target" &&
                wallAssignments.index === i,
              targetActive: unlocked,
              canAssign: true,
              visualWhenInactive: "locked",
            });
      const effectiveState = wallMpState?.operative
        ? wallMpState
        : directMpState;
      nodes.element.dataset.unlocked = unlocked ? "true" : "false";
      nodes.element.dataset.hovering = hoveringIndex === i ? "true" : "false";
      nodes.element.dataset.mpAssigned = effectiveState.assigned
        ? "true"
        : "false";
      nodes.element.dataset.mpOperative = effectiveState.operative
        ? "true"
        : "false";
      nodes.element.dataset.mpBlocked = effectiveState.mpBlocked
        ? "true"
        : "false";
      if (effectiveState.mpBlockedReason !== undefined) {
        nodes.element.dataset.mpBlockedReason = effectiveState.mpBlockedReason;
      } else {
        delete nodes.element.dataset.mpBlockedReason;
      }
      nodes.element.dataset.blocked = effectiveState.mpBlocked
        ? "true"
        : "false";
      const dashCycleDurationMs = resolveTimConsumptionDashCycleDurationMs(
        this.getTimHovConsumptionRatePerSecond?.(snapshot, i) ?? 0,
      );
      nodes.element.dataset.timConsuming =
        dashCycleDurationMs === null ? "false" : "true";
      if (dashCycleDurationMs === null) {
        nodes.element.style.removeProperty(
          "--tim-consumption-dash-cycle-duration",
        );
      } else {
        nodes.element.style.setProperty(
          "--tim-consumption-dash-cycle-duration",
          `${Math.round(dashCycleDurationMs)}ms`,
        );
      }
      nodes.element.style.setProperty("--cell-progress", progress.toFixed(3));
      nodes.lockBadge.hidden = unlocked;

      if (nodes.mpSlot !== null) syncDirectMpSlot(nodes.mpSlot, directMpState);
      if (nodes.wallMpSlot !== null)
        syncDirectMpSlot(nodes.wallMpSlot, wallMpState);
    }
    // Apply every DOM/snapshot interactivity flag before controller callbacks
    // can trigger a re-entrant render. Disable first so stale model hover is
    // cleared before any newly interactive target is evaluated.
    for (let i = 0; i < TOTAL_CELLS; i += 1) {
      if (!this.cellInteractivity[i]) {
        this.cells[i].intent.setEnabled(false);
      }
    }
    for (let i = 0; i < TOTAL_CELLS; i += 1) {
      if (this.cellInteractivity[i]) {
        this.cells[i].intent.setEnabled(true);
      }
    }
    if (
      (gridTierChanged || interactivityChanged) &&
      options.refreshHoverIntentsAfterStructureChange !== false
    ) {
      this.refreshHoverIntentsAfterGridStructureChange(hoveringIndex);
    }
  }

  updateThemeHue(themeHue: number | null): void {
    applyHovThemeHue(this.root, this.variant === "hov" ? themeHue : null);
  }

  refreshHoverIntents(): void {
    this.refreshHoverIntentsAfterGridStructureChange(null);
  }

  restoreVirtualPointerFromHoverAnchor(): boolean {
    if (this.isInputLocked) {
      return false;
    }
    const restored = this.pointerModel.restoreVirtualTouchAnchorPosition(
      (targetId) => {
        for (const nodes of this.cells) {
          if (nodes.element.dataset.hoverAnchorTargetId !== targetId) {
            continue;
          }
          if (nodes.element.hidden) {
            return null;
          }
          const rect = nodes.element.getBoundingClientRect();
          return {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
          };
        }
        return null;
      },
    );
    if (restored) {
      this.refreshHoverIntents();
    }
    return restored;
  }

  private refreshHoverIntentsAfterGridStructureChange(
    previousHoveringCellIndex: number | null,
  ): void {
    if (this.isInputLocked) {
      return;
    }
    for (const index of orderHoverTargetRefreshIndices(
      this.cells.length,
      previousHoveringCellIndex,
    )) {
      const nodes = this.cells[index];
      if (nodes.element.hidden || !this.cellInteractivity[index]) {
        continue;
      }
      // Reset can clear model hover while the pointer remains inside. Force
      // re-emission for tier layout and same-tier interactivity changes alike.
      nodes.intent.refresh({ forceEmit: true });
      for (const slot of [nodes.mpSlot, nodes.wallMpSlot]) {
        if (slot !== null && !slot.element.hidden) slot.controller.refresh();
      }
    }
  }

  setInputLocked(locked: boolean): void {
    if (locked === this.isInputLocked) {
      return;
    }
    this.isInputLocked = locked;
    this.root.dataset.inputLocked = locked ? "true" : "false";
    for (const nodes of this.cells) {
      if (locked) {
        nodes.intent.clearPointerHover();
      }
      nodes.mpSlot?.controller.setInputLocked(locked);
      nodes.wallMpSlot?.controller.setInputLocked(locked);
    }
  }

  dispose(): void {
    for (const nodes of this.cells) {
      nodes.intent.dispose();
      nodes.cleanup();
      nodes.mpSlot?.controller.dispose();
      nodes.wallMpSlot?.controller.dispose();
    }
    this.root.remove();
  }

  private resolveCellProgress(
    cellIndex: number,
    purchasedUpgrades: number,
  ): number {
    if (this.variant === "hov") {
      return Math.min((cellIndex + 1) / HOV_CELL_COLOR_STEPS, 0.9);
    }
    return Math.min(purchasedUpgrades / COLOR_PROGRESS_MAX_LEVEL, 1);
  }
}
